import { AuBankAccountElementProps, CardElementProps } from "@stripe/react-stripe-js";
import { z } from "zod";
import { BankRoutingScheme, getBankRoutingScheme } from "@/helpers/bankRoutingSchemes";

export const auBankAccountOptions: AuBankAccountElementProps["options"] = {
  classes: {
    base: "flex flex-col p-[10px] rounded-lg bg-white text-neutral-black border border-neutral-mid-gray placeholder-neutral-dark-gray",
    focus: "border-primary shadow-input ring-0",
    invalid: "!border-danger",
  },
  disabled: false,
  hideIcon: false,
  iconStyle: "default",
};

/** Same chrome as the AU bank-account element, for the platform-fee card in card-only countries. */
export const cardElementOptions: CardElementProps["options"] = {
  classes: auBankAccountOptions?.classes,
  disabled: false,
  hidePostalCode: true,
};

/**
 * The routing identifier's format is a property of the country's banking scheme, so the schema is
 * built per scheme: a BSB is 3-3 digits, a sort code 2-2-2, an ABA routing number 9. The previous
 * single schema validated a BSB for every non-US country, so a UK operator could submit a
 * wrong-format sort code and be told it was fine.
 */
export const createDepositsFormSchema = (scheme?: BankRoutingScheme | string) => {
  const { pattern, errorMessage, accountNumberLength } = getBankRoutingScheme(scheme);
  const { min, max } = accountNumberLength;

  const accountNumber = z
    .string()
    .nonempty("Field is required and can't be empty.")
    .min(min, `Minimum of ${min} characters`)
    .max(max, `Maximum of ${max} characters`);

  return z
    .object({
      bsb: z.string().regex(pattern, errorMessage),
      accountNumber,
      confirmAccountNumber: accountNumber,
    })
    .refine((data) => data.accountNumber === data.confirmAccountNumber, {
      message: "Account numbers don't match. Please try again.",
      path: ["confirmAccountNumber"],
    });
};

/** @deprecated Use createDepositsFormSchema with the country's routing scheme. */
export const depositsFormSchema = createDepositsFormSchema("bsb");
